import { useEffect, useMemo, useState } from "react"
import PhoneInput, { isValidPhoneNumber, type Value } from "react-phone-number-input"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { extractApiError } from "@/lib/api-errors"
import { CalThrottledNotice } from "@/components/CalThrottledNotice"
import { isCalThrottled } from "@/lib/calThrottled"
import { useAppSelector } from "@/store"
import { useCreateBookingAsStaffMutation } from "../bookingCalendarApi"
import type { NewBookingTarget } from "../bookingCalendarSlice"
import { isOutsideBookingSeason } from "../lib/bookingSeason"
import type { BookingSeasonBounds } from "../lib/bookingSeason"
import {
  HOST_ALREADY_BOOKED_CODE,
  doubleBookingFields,
  isHostAlreadyBooked,
  isOverrideReasonValid,
} from "../lib/doubleBooking"
import { DAILY_LIMIT_REACHED_CODE, dailyLimitFields, isDailyLimitRefusal } from "../lib/dailyLimit"
import { minutesToLabel, zonedTimeToInstant } from "../lib/siteTime"
import { SLOT_TYPE_LABEL, canOverrideBookingRefusals, eventTypeSlugFor } from "../lib/slotTypes"
import type { SlotType } from "../lib/slotTypes"
import { DailyLimitOverrideSection } from "./DailyLimitOverrideSection"
import { DoubleBookOverrideSection } from "./DoubleBookOverrideSection"

// The public flow's own predicates, copied rather than imported so the two forms cannot silently
// disagree about what a valid answer is — see IdentifyingInfoStep.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const SSN_PATTERN = /^\d{9}$/

/** Matches the server's Range(1, 24) on household size. */
const MAX_HOUSEHOLD_SIZE = 24

const PHONE_INPUT_CLASS =
  "border-input dark:bg-input/30 [&.PhoneInput--focus]:border-ring [&.PhoneInput--focus]:ring-ring/50 " +
  "[&.PhoneInput--focus]:ring-[3px] flex h-9 w-full min-w-0 items-center gap-2 rounded-md border " +
  "bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none md:text-sm"

const PHONE_NUMBER_INPUT_PROPS = {
  className:
    "placeholder:text-muted-foreground min-w-0 flex-1 border-0 bg-transparent p-0 text-base shadow-none outline-none md:text-sm",
}

/**
 * Books somebody into an open slot, on their behalf.
 *
 * The counter's version of the public booking flow, and much shorter than it on purpose: an
 * applicant standing at a desk with a shut-off notice can give a name and a number, and often not
 * much else. First name, last name and a phone number are the whole requirement — the server applies
 * a contact-only completeness rule on this path, so the household questions below are genuinely
 * optional rather than optional-until-you-submit.
 *
 * The host is not a choice here. The dialog opens from one person's column on one day, so who it is
 * for is already settled by the click; a picker would only be a way to disagree with it.
 */
export function NewBookingDialog({ target, hostName, teamSlug, timeZone, slotMinutes, bookingSeason, onClose }: {
  /** The slot being booked into. Undefined closes the dialog, as with AppointmentDialog. */
  target: NewBookingTarget | undefined
  /** The host, as the grid labels them. */
  hostName: string | undefined
  /** The site's Cal team slug. Undefined for a team Cal never slugged, which can take no bookings. */
  teamSlug: string | undefined
  timeZone: string
  /** The grid's row length, used only to show the appointment's end time. */
  slotMinutes: number
  /**
   * When the public may book, to say so when this slot is outside it. Never a reason to refuse:
   * staff book whenever they need to. Undefined while unknown, which says nothing.
   */
  bookingSeason?: BookingSeasonBounds
  onClose: () => void
}) {
  const [createBooking, createState] = useCreateBookingAsStaffMutation()

  // Every override — double-booking a host, booking a household that already has an appointment
  // ("Book anyway") and booking past the daily limit (AB#606) — is appointments:manage's (AB#779).
  // The endpoint is open to appointments:book as well and refuses each flag from it with a 403, so this decides whether a refusal offers a
  // way through or is just shown.
  const { permissions } = useAppSelector(state => state.permissions)
  const canOverride = canOverrideBookingRefusals(permissions)

  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [phone, setPhone] = useState<Value | undefined>(undefined)
  const [email, setEmail] = useState("")
  const [dateOfBirth, setDateOfBirth] = useState("")
  const [ssn, setSsn] = useState("")
  const [smsOptIn, setSmsOptIn] = useState(false)
  const [energyAccountNumber, setEnergyAccountNumber] = useState("")

  const [address, setAddress] = useState("")
  const [householdSize, setHouseholdSize] = useState("")
  const [householdIncome, setHouseholdIncome] = useState("")
  const [energySource, setEnergySource] = useState("")
  const [pseService, setPseService] = useState("")

  const [showHousehold, setShowHousehold] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** Cal throttled the booking (503 calThrottled, AB#514): the busy notice instead of `error`. */
  const [throttled, setThrottled] = useState<unknown>(null)

  /** Armed only after the server has said this household already has an appointment. */
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null)

  /**
   * Armed only after the server has said the host is already booked then (409 hostAlreadyBooked),
   * and only for somebody who may double-book. Mirrors `duplicateWarning`, plus a required reason.
   */
  const [doubleBookWarning, setDoubleBookWarning] = useState<string | null>(null)

  /**
   * Armed only after the server has said the host is at their daily limit that day (409
   * dailyLimitReached, AB#606), and only for somebody who may override. Same shape as the double
   * booking's, and the same reason: the server takes one `overrideReason` for both.
   */
  const [dailyLimitWarning, setDailyLimitWarning] = useState<string | null>(null)

  /** The one reason the overrides share — what `overrideReason` carries. */
  const [doubleBookReason, setDoubleBookReason] = useState("")

  // Which overrides the user has already chosen. Kept across the retry, because the server asks about
  // the host before the household: a double booking can still meet the duplicate-household check, and
  // agreeing to that must not quietly drop the double booking agreed to a moment before.
  const [duplicateArmed, setDuplicateArmed] = useState(false)
  const [doubleBookArmed, setDoubleBookArmed] = useState(false)
  const [dailyLimitArmed, setDailyLimitArmed] = useState(false)

  /** Set when Cal gave the appointment to somebody other than the host that was asked for. */
  const [hostMismatch, setHostMismatch] = useState<string | null>(null)

  // Identifies this booking attempt, and must stay the same across every retry of it: it is the
  // idempotency key, so a double-clicked submit collapses to one appointment instead of two.
  // Regenerated per slot below, because a second booking is a different attempt, and after a refusal
  // in submit(), because so is the corrected one.
  const [bookingKey, setBookingKey] = useState(() => newBookingKey())

  const slotId = target ? `${target.columnId}:${target.date}:${target.startMinutes}` : null

  // A dialog reopened on another slot must not carry the last one's half-typed form, its armed
  // override, or — worst of the three — its booking key, which would make the second booking
  // collapse into the first.
  useEffect(() => {
    setFirstName("")
    setLastName("")
    setPhone(undefined)
    setEmail("")
    setDateOfBirth("")
    setSsn("")
    setSmsOptIn(false)
    setEnergyAccountNumber("")
    setAddress("")
    setHouseholdSize("")
    setHouseholdIncome("")
    setEnergySource("")
    setPseService("")
    setShowHousehold(false)
    setError(null)
    setThrottled(null)
    setDuplicateWarning(null)
    setDoubleBookWarning(null)
    setDailyLimitWarning(null)
    setDoubleBookReason("")
    setDuplicateArmed(false)
    setDoubleBookArmed(false)
    setDailyLimitArmed(false)
    setHostMismatch(null)
    setBookingKey(newBookingKey())
  }, [slotId])

  const phoneValid = !!phone && isValidPhoneNumber(phone) && phone.replace(/\D/g, "").length <= 11
  const emailValid = email === "" || EMAIL_PATTERN.test(email)
  const ssnValid = ssn === "" || SSN_PATTERN.test(ssn)
  const sizeValid = householdSize === "" || (Number(householdSize) >= 1 && Number(householdSize) <= MAX_HOUSEHOLD_SIZE)
  const incomeValid = householdIncome === "" || Number(householdIncome) >= 0

  const canSubmit =
    firstName.trim() !== "" &&
    lastName.trim() !== "" &&
    phoneValid &&
    emailValid &&
    ssnValid &&
    sizeValid &&
    incomeValid &&
    !!teamSlug

  const when = useMemo(() => {
    if (!target) return null
    return {
      start: minutesToLabel(target.startMinutes),
      end: minutesToLabel(target.startMinutes + slotMinutes),
    }
  }, [target, slotMinutes])

  // The same question the grid asked of the cell that was clicked, asked again of the slot itself
  // rather than carried over: the target is a date and a time, not a cell.
  const outsideSeason = useMemo(
    () => !!target && isOutsideBookingSeason(
      zonedTimeToInstant(target.date, target.startMinutes, timeZone), bookingSeason),
    [target, timeZone, bookingSeason])

  if (!target) return null

  async function submit(choice: { overrideDuplicate?: boolean; doubleBook?: boolean; pastDailyLimit?: boolean } = {}) {
    if (!target || !teamSlug) return

    const overrideDuplicate = choice.overrideDuplicate ?? duplicateArmed
    const doubleBook = choice.doubleBook ?? doubleBookArmed
    const pastDailyLimit = choice.pastDailyLimit ?? dailyLimitArmed
    if ((doubleBook || pastDailyLimit) && !isOverrideReasonValid(doubleBookReason)) return
    setDuplicateArmed(overrideDuplicate)
    setDoubleBookArmed(doubleBook)
    setDailyLimitArmed(pastDailyLimit)

    setError(null)
    setThrottled(null)
    setHostMismatch(null)

    const household = {
      householdSize: householdSize === "" ? undefined : Number(householdSize),
      householdIncome: householdIncome === "" ? undefined : Number(householdIncome),
      address: address.trim() || undefined,
      energySource: energySource || undefined,
      pseService: energySource === "PSE" ? pseService || undefined : undefined,
    }

    // Omitted entirely when nothing was answered, so "no household given" and "a household of
    // nobody" cannot be confused on the wire.
    const anyHousehold = Object.values(household).some(value => value !== undefined)

    const result = await createBooking({
      teamSlug,
      eventTypeSlug: eventTypeSlugFor(target.slotType),
      start: zonedTimeToInstant(target.date, target.startMinutes, timeZone).toISOString(),
      hostCalUserId: target.calUserId,
      bookingKey,
      attendee: {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phone!,
        email: email.trim() || undefined,
        timeZone,
        dateOfBirth: dateOfBirth || undefined,
        ssn: ssn || undefined,
        smsOptIn,
        energyAccountNumber: energyAccountNumber.trim() || undefined,
      },
      household: anyHousehold ? household : undefined,
      overrideDuplicate: overrideDuplicate || undefined,
      ...doubleBookingFields(doubleBook, doubleBookReason),
      ...dailyLimitFields(pastDailyLimit, doubleBookReason),
    })

    if ("error" in result) {
      const failure = result.error as { status?: number | string; data?: unknown }
      const body = failure?.data as { code?: string } | undefined

      // A 4xx is the server saying no, having booked nothing, so whatever is submitted next — the
      // typo fixed, or "book anyway" — is a new attempt and must not be filed against the refused
      // one. Kept for a network failure or a 5xx, where the appointment may have been made and a
      // retry has to collapse into it.
      if (typeof failure?.status === "number" && failure.status >= 400 && failure.status < 500) {
        setBookingKey(newBookingKey())
      }

      // Cal turned the request away before booking anything (AB#514). Not a refusal: the same answers,
      // overrides and booking key go again from the notice once the wait is over.
      if (isCalThrottled(result.error)) {
        setThrottled(result.error)
        return
      }

      // The two refusals that offer a way forward rather than just a message. The host one only to
      // somebody who may double-book; anybody else is just told.
      if (failure?.status === 409 && body?.code === "householdAlreadyBooked") {
        setDoubleBookWarning(null)
        setDailyLimitWarning(null)
        setDuplicateWarning(
          extractApiError(failure.data) || "An upcoming appointment already exists for this household.")
        return
      }

      if (canOverride && isHostAlreadyBooked(result.error)) {
        setDuplicateWarning(null)
        setDailyLimitWarning(null)
        setDoubleBookWarning(extractApiError(failure.data) || "That host already has an appointment at this time.")
        return
      }

      // Asked before the host's other appointments, so it is the first a capped host meets; the
      // override agreed to here is kept if the retry then meets a double booking (AB#606).
      if (canOverride && isDailyLimitRefusal(result.error)) {
        setDuplicateWarning(null)
        setDoubleBookWarning(null)
        setDailyLimitWarning(extractApiError(failure.data) || "That host has already reached their daily limit on this day.")
        return
      }

      // Anything else starts over: an override agreed to for one refusal is not carried into a
      // different one.
      setDuplicateWarning(null)
      setDoubleBookWarning(null)
      setDailyLimitWarning(null)
      setDuplicateArmed(false)
      setDoubleBookArmed(false)
      setDailyLimitArmed(false)
      setError(describeCreateFailure(result.error))
      return
    }

    // The appointment exists either way. When Cal put it on somebody else, say so and make the user
    // dismiss it: closing quietly would leave an administrator believing they had booked a colleague
    // who has never heard of it.
    if (!result.data.hostAsRequested) {
      setHostMismatch(
        result.data.assignedHostCalUserId
          ? "The appointment was created, but the scheduling system assigned it to a different host. Check the grid before telling the applicant who they are seeing."
          : "The appointment was created, but which host it went to could not be confirmed. Check the grid before telling the applicant who they are seeing.")
      return
    }

    onClose()
  }

  const busy = createState.isLoading
  const footer = newBookingFooter({
    hostMismatch: !!hostMismatch,
    doubleBookWarning: !!doubleBookWarning,
    dailyLimitWarning: !!dailyLimitWarning,
    duplicateWarning: !!duplicateWarning,
    canOverride,
  })

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose() }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {newBookingTitle(target.slotType)}
          </DialogTitle>
          <DialogDescription>
            {when && `${when.start} – ${when.end}`}
            {hostName && ` · ${hostName}`}
          </DialogDescription>
        </DialogHeader>

        {/* Said, not enforced: the season holds the public back and never staff, so this changes
            nothing about whether the form submits. Worth saying because whoever is booking may be
            telling the household it can book the next one online. */}
        {outsideSeason && (
          <p role="note" className="rounded-md border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            This time is outside the booking season. The public can't book it, but you can.
          </p>
        )}

        {/* Nothing below is asked twice. The host and the time came from the cell that was clicked,
            and offering them again would only be a way to disagree with it. */}
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="nb-first">First name <span aria-hidden className="text-destructive">*</span></Label>
              <Input id="nb-first" value={firstName} disabled={busy} autoComplete="off"
                onChange={e => setFirstName(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="nb-last">Last name <span aria-hidden className="text-destructive">*</span></Label>
              <Input id="nb-last" value={lastName} disabled={busy} autoComplete="off"
                onChange={e => setLastName(e.target.value)} />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="nb-phone">Phone number <span aria-hidden className="text-destructive">*</span></Label>
            <PhoneInput
              id="nb-phone"
              defaultCountry="US"
              placeholder="Enter phone number"
              value={phone}
              disabled={busy}
              onChange={setPhone}
              className={PHONE_INPUT_CLASS}
              numberInputProps={PHONE_NUMBER_INPUT_PROPS}
            />
          </div>

          {/* Never pre-ticked: an untouched box means they did not ask to be texted. */}
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={smsOptIn} disabled={busy}
              onCheckedChange={checked => setSmsOptIn(checked === true)} />
            They agreed to be texted about this appointment
          </label>

          <div className="grid gap-1.5">
            <Label htmlFor="nb-email">Email <span className="text-muted-foreground">(optional)</span></Label>
            <Input id="nb-email" type="email" value={email} disabled={busy} autoComplete="off"
              onChange={e => setEmail(e.target.value)} />
            {!emailValid && <p className="text-xs text-red-700">Enter a valid email address.</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="nb-dob">Date of birth <span className="text-muted-foreground">(optional)</span></Label>
              <Input id="nb-dob" type="date" value={dateOfBirth} disabled={busy}
                onChange={e => setDateOfBirth(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="nb-ssn">SSN <span className="text-muted-foreground">(optional)</span></Label>
              {/* Masked and never autofilled. The server encrypts it and it never comes back. */}
              <Input id="nb-ssn" type="password" inputMode="numeric" autoComplete="off"
                placeholder="9 digits" value={ssn} disabled={busy}
                onChange={e => setSsn(e.target.value.replace(/\D/g, "").slice(0, 9))} />
              {!ssnValid && <p className="text-xs text-red-700">Enter all nine digits, or leave blank.</p>}
            </div>
          </div>

          {/* Up here with the identifiers rather than down in the household section, because that is
              what it is: it is on file for nearly every PSE customer and it is what the benefit
              screening matches on. Unvalidated on purpose — providers number accounts differently,
              so a format check would reject real ones. */}
          <div className="grid gap-1.5">
            <Label htmlFor="nb-energy-account">
              Energy account number <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input id="nb-energy-account" value={energyAccountNumber} disabled={busy} autoComplete="off"
              onChange={e => setEnergyAccountNumber(e.target.value.slice(0, 50))} />
          </div>

          {/* Collapsed by default. These are the questions the public flow spends a screen on, and a
              booking taken at a counter usually has none of the answers yet — putting them behind a
              disclosure keeps the required three at the top where they are actually filled in. */}
          <div className="border-t pt-3">
            <Button type="button" variant="ghost" size="sm" className="px-0 text-xs"
              onClick={() => setShowHousehold(v => !v)}>
              {showHousehold ? "Hide" : "Add"} household details (optional)
            </Button>
          </div>

          {showHousehold && (
            <div className="grid gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="nb-address">Address</Label>
                <Input id="nb-address" value={address} disabled={busy}
                  onChange={e => setAddress(e.target.value)} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="nb-size">Household size</Label>
                  <Input id="nb-size" type="number" min={1} max={MAX_HOUSEHOLD_SIZE}
                    value={householdSize} disabled={busy}
                    onChange={e => setHouseholdSize(e.target.value)} />
                  {!sizeValid && <p className="text-xs text-red-700">Between 1 and {MAX_HOUSEHOLD_SIZE}.</p>}
                </div>
                <div className="grid gap-1.5">
                  {/* Monthly, not annual — the label says which, because the figure is easy to read
                      as the wrong one. */}
                  <Label htmlFor="nb-income">Monthly income</Label>
                  <Input id="nb-income" type="number" min={0} value={householdIncome} disabled={busy}
                    onChange={e => setHouseholdIncome(e.target.value)} />
                  {!incomeValid && <p className="text-xs text-red-700">Cannot be negative.</p>}
                </div>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="nb-energy">Energy source</Label>
                <Select value={energySource} disabled={busy} onValueChange={setEnergySource}>
                  <SelectTrigger id="nb-energy"><SelectValue placeholder="Not given" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PSE">PSE</SelectItem>
                    <SelectItem value="Other Heating and Energy Source">Other heating and energy source</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {energySource === "PSE" && (
                <div className="grid gap-1.5">
                  <Label htmlFor="nb-pse">PSE service</Label>
                  <Select value={pseService} disabled={busy} onValueChange={setPseService}>
                    <SelectTrigger id="nb-pse"><SelectValue placeholder="Not given" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Gas">Gas</SelectItem>
                      <SelectItem value="Electric">Electric</SelectItem>
                      <SelectItem value="Both gas and electric">Both gas and electric</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
        </div>

        {!teamSlug && (
          <p role="alert" className="text-sm text-red-700">
            This team is not set up in the scheduling system, so it cannot take bookings.
          </p>
        )}

        {duplicateWarning && (
          <section className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:bg-amber-950/30">
            <p className="text-sm font-medium">{duplicateWarning}</p>
            {/* The override is attributable: it is logged against the staff member and written into
                the booking, which is what makes it defensible at all. Said here so whoever clicks it
                knows that before they do. Somebody who may not override is told who can instead. */}
            <p className="mt-1 text-xs text-muted-foreground">
              {canOverride
                ? "Booking anyway is recorded against your name on the appointment."
                : "Only an appointments manager can book this household again."}
            </p>
          </section>
        )}

        {doubleBookWarning && (
          <DoubleBookOverrideSection
            id="nb-double-book-reason"
            message={doubleBookWarning}
            reason={doubleBookReason}
            onReasonChange={setDoubleBookReason}
            disabled={busy}
          />
        )}

        {dailyLimitWarning && (
          <DailyLimitOverrideSection
            id="nb-daily-limit-reason"
            message={dailyLimitWarning}
            reason={doubleBookReason}
            onReasonChange={setDoubleBookReason}
            disabled={busy}
          />
        )}

        {hostMismatch && <p role="alert" className="text-sm text-amber-700">{hostMismatch}</p>}

        {throttled ? (
          <CalThrottledNotice error={throttled} onRetry={() => void submit()} />
        ) : error && <p role="alert" className="text-sm text-red-700">{error}</p>}

        <DialogFooter className="sm:flex-wrap sm:items-center">
          {footer === "close" ? (
            <Button onClick={onClose}>Close</Button>
          ) : footer === "doubleBookAnyway" ? (
            <>
              <Button variant="outline" disabled={busy} onClick={onClose}>Cancel</Button>
              <Button
                variant="destructive"
                disabled={busy || !isOverrideReasonValid(doubleBookReason)}
                onClick={() => submit({ doubleBook: true })}
              >
                {busy ? "Booking…" : "Double-book anyway"}
              </Button>
            </>
          ) : footer === "bookPastDailyLimit" ? (
            <>
              <Button variant="outline" disabled={busy} onClick={onClose}>Cancel</Button>
              <Button
                variant="destructive"
                disabled={busy || !isOverrideReasonValid(doubleBookReason)}
                onClick={() => submit({ pastDailyLimit: true })}
              >
                {busy ? "Booking…" : "Book past daily limit"}
              </Button>
            </>
          ) : footer === "bookAnyway" ? (
            <>
              <Button variant="outline" disabled={busy} onClick={onClose}>Cancel</Button>
              <Button variant="destructive" disabled={busy} onClick={() => submit({ overrideDuplicate: true })}>
                {busy ? "Booking…" : "Book anyway"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" disabled={busy} onClick={onClose}>Cancel</Button>
              <Button disabled={!canSubmit || busy} onClick={() => submit()}>
                {busy ? "Booking…" : "Book appointment"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The dialog's title: the slot type it books (AB#779), with the ordinary public one unqualified. */
export function newBookingTitle(slotType: SlotType): string {
  return slotType === "public" ? "New appointment" : `New ${SLOT_TYPE_LABEL[slotType].toLowerCase()} appointment`
}

/**
 * Which buttons the dialog ends with. The two override buttons — "Double-book anyway" and "Book
 * anyway" — only for somebody who may override (`appointments:manage`, AB#779); anybody else who
 * meets either refusal keeps the ordinary buttons, so they can correct the form or cancel, and is
 * told by the warning who can book it instead.
 */
export type NewBookingFooter = "close" | "doubleBookAnyway" | "bookPastDailyLimit" | "bookAnyway" | "book"

export function newBookingFooter(state: {
  hostMismatch: boolean
  doubleBookWarning: boolean
  /** The server said the host is at their daily limit (AB#606); "Book past daily limit" is manage's. */
  dailyLimitWarning?: boolean
  duplicateWarning: boolean
  canOverride: boolean
}): NewBookingFooter {
  if (state.hostMismatch) return "close"
  if (state.doubleBookWarning && state.canOverride) return "doubleBookAnyway"
  if (state.dailyLimitWarning && state.canOverride) return "bookPastDailyLimit"
  if (state.duplicateWarning && state.canOverride) return "bookAnyway"
  return "book"
}

/** Identifies one booking attempt. Prefixed so a CMS row's reservation uid says where it came from. */
function newBookingKey(): string {
  return `staff-${crypto.randomUUID()}`
}

/** Turns a failed booking into something worth reading. */
export function describeCreateFailure(error: unknown): string {
  const failure = error as { status?: number | string; data?: unknown }
  const body = failure?.data as { code?: string } | undefined
  const detail = extractApiError(failure?.data)

  // The server says which permission was missing — a managed slot, or one of the overrides — and
  // that is worth more than the generic line.
  if (failure?.status === 403) return detail || "You do not have permission to book appointments."

  // Every site sets an eight-hour minimum booking notice and the scheduling system enforces it, but
  // the grid still draws those slots as open. Only sent when the scheduling system actually named
  // that rule — it answers the same status for several unrelated refusals, and the API now tells
  // them apart rather than blaming this one for all of them.
  if (body?.code === "outsideBookingWindow") {
    return detail || "Appointments cannot be booked less than 8 hours ahead."
  }

  if (body?.code === "slotUnavailable") {
    return detail || "That slot is no longer free for this person."
  }

  if (body?.code === HOST_ALREADY_BOOKED_CODE) {
    return detail || "That host already has an appointment at this time."
  }

  // What somebody who may not override sees (AB#606): the reason, and who can get past it.
  if (body?.code === DAILY_LIMIT_REACHED_CODE) {
    return `${detail || "That host has already reached their daily limit on this day."} Only an appointments manager can book past it.`
  }

  if (detail) return detail

  return "The appointment could not be created."
}
